import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';

interface BrushStrokeProps {
  minInterval?: number; // Minimum seconds between strokes
  maxInterval?: number; // Maximum seconds between strokes
}

export const BrushStrokeAnimation: React.FC<BrushStrokeProps> = ({
  minInterval = 3,
  maxInterval = 8,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [strokePath, setStrokePath] = useState<string>('');
  const [filterSeed, setFilterSeed] = useState<number>(0); // Vary filter texture per stroke
  // Removed strokeWidthVariation - was causing pop effect
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect mobile device for performance optimization
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 ||
                            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate a random curved path that enters and exits the frame
  const generateStrokePath = () => {
    // Use viewBox dimensions for consistent scaling
    const containerWidth = 1920;
    const containerHeight = 600;

    // Randomly choose entry side: 0: top, 1: right, 2: bottom, 3: left
    const entrySide = Math.floor(Math.random() * 4);
    let startX: number, startY: number;

    // Exit from a different side (not the same as entry)
    let exitSide: number;
    do {
      exitSide = Math.floor(Math.random() * 4);
    } while (exitSide === entrySide);
    let endX: number, endY: number;

    // Control points for smooth arc
    let controlX1: number, controlY1: number, controlX2: number, controlY2: number;

    // Ensure start and end are always well off-screen
    const offScreenDistance = 100;

    // Determine entry point based on side
    if (entrySide === 0) {
      // Top
      startX = Math.random() * containerWidth;
      startY = -offScreenDistance;
    } else if (entrySide === 1) {
      // Right
      startX = containerWidth + offScreenDistance;
      startY = Math.random() * containerHeight;
    } else if (entrySide === 2) {
      // Bottom
      startX = Math.random() * containerWidth;
      startY = containerHeight + offScreenDistance;
    } else {
      // Left
      startX = -offScreenDistance;
      startY = Math.random() * containerHeight;
    }

    // Determine exit point based on side
    if (exitSide === 0) {
      // Top
      endX = Math.random() * containerWidth;
      endY = -offScreenDistance;
    } else if (exitSide === 1) {
      // Right
      endX = containerWidth + offScreenDistance;
      endY = Math.random() * containerHeight;
    } else if (exitSide === 2) {
      // Bottom
      endX = Math.random() * containerWidth;
      endY = containerHeight + offScreenDistance;
    } else {
      // Left
      endX = -offScreenDistance;
      endY = Math.random() * containerHeight;
    }

    // Create tighter circular arcs with smaller diameter
    // Calculate a midpoint that creates a smaller, tighter circular arc
    const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    const angle = Math.atan2(endY - startY, endX - startX);
    const perpendicularAngle = angle + Math.PI / 2;

    // Increase perpendicular offset to make arcs feel more circular/pronounced
    // 0.85 * chord length gives a tighter, more obvious circular sweep
    const arcDistance = distance * 0.85;
    const direction = Math.random() > 0.5 ? 1 : -1;
    const midX = (startX + endX) / 2 + Math.cos(perpendicularAngle) * arcDistance * direction;
    const midY = (startY + endY) / 2 + Math.sin(perpendicularAngle) * arcDistance * direction;

    // Control points tuned for a more circular feel
    controlX1 = startX + (midX - startX) * 0.4;
    controlY1 = startY + (midY - startY) * 0.4;
    controlX2 = midX + (endX - midX) * 0.6;
    controlY2 = midY + (endY - midY) * 0.6;

    // Create cubic bezier path
    const path = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

    return path;
  };

  const triggerStroke = () => {
    const path = generateStrokePath();
    setStrokePath(path);
    setFilterSeed(Math.random() * 1000); // Vary texture per stroke for realism
    setIsVisible(true);

    // Hide after animation completes (2s draw + 3s fade = 5s total)
    setTimeout(() => {
      setIsVisible(false);
    }, 5000);

    // Schedule next stroke - ensure it starts AFTER current stroke fully fades
    // Add buffer time to prevent overlap (5s animation + 1-2s buffer + random interval)
    const animationDuration = 5000; // 5 seconds total
    const bufferTime = (Math.random() * 1000 + 1000); // 1-2 second buffer
    const randomInterval = (Math.random() * (maxInterval - minInterval) + minInterval) * 1000;
    const nextInterval = animationDuration + bufferTime + randomInterval;
    timeoutRef.current = setTimeout(triggerStroke, nextInterval);
  };

  useEffect(() => {
    // Initial delay before first stroke (longer to ensure clean start)
    const initialDelay = (Math.random() * (maxInterval - minInterval) + minInterval + 2) * 1000;
    timeoutRef.current = setTimeout(triggerStroke, initialDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // triggerStroke is stable, min/maxInterval are props that don't change

  // Static SVG defs - optimized for mobile performance
  const svgDefs = useMemo(() => {
    // Mobile: reduced complexity (fewer octaves, less blur, less displacement)
    // Desktop: full effects with increased roughness
    const turbulenceOctaves = isMobile ? 1 : 2;
    const baseFrequency = isMobile ? 0.6 : 0.8; // Slightly higher for more texture
    const displacementScale = isMobile ? 2.0 : 3.5; // Increased roughness on desktop
    const maxBlur = isMobile ? 3 : 5; // Less blur on mobile

    return (
      <defs>
        {/* Mobile filter: texture only, no blur for performance */}
        <filter id="brushMobile" x="-50%" y="-50%" width="200%" height="200%">
          <feTurbulence
            baseFrequency={baseFrequency}
            numOctaves={turbulenceOctaves}
            seed={filterSeed}
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={displacementScale}
          />
        </filter>
        {/* Desktop filter: texture + animated blur bleed */}
        <filter id="brushDesktop" x="-50%" y="-50%" width="200%" height="200%">
          <feTurbulence
            baseFrequency={baseFrequency}
            numOctaves={turbulenceOctaves}
            seed={filterSeed}
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={displacementScale}
            result="textured"
          />
          <feGaussianBlur
            in="textured"
            stdDeviation="0"
            result="blurred"
          >
            <animate
              attributeName="stdDeviation"
              values={`0;0;1.5;3;${maxBlur}`}
              dur="5s"
              begin="0s"
              fill="freeze"
            />
          </feGaussianBlur>
        </filter>
        {/* Gradient for fade effect along the path - reversed: opaque at front, fades at back */}
        <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(0, 0, 0, 0.95)" />
          <stop offset="30%" stopColor="rgba(0, 0, 0, 0.7)" />
          <stop offset="70%" stopColor="rgba(0, 0, 0, 0.3)" />
          <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
        </linearGradient>
      </defs>
    );
  }, [filterSeed, isMobile]);

  return (
    <div className="brush-stroke-container">
      {/* Always render SVG with defs for better performance */}
      <svg
        className="brush-stroke-svg"
        viewBox="0 0 1920 600"
        preserveAspectRatio="xMidYMid slice"
        style={{ visibility: isVisible ? 'visible' : 'hidden' }}
      >
        {svgDefs}
        {isVisible && (
          <path
            d={strokePath}
            className="brush-stroke-path"
            stroke="url(#strokeGradient)"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={isMobile ? "url(#brushMobile)" : "url(#brushDesktop)"}
          />
        )}
      </svg>
    </div>
  );
};
